import { computed, ref } from 'vue'
import { useAddToCartMutation, useCartQuery, useCreateGuestSessionMutation, useGuestSessionQuery } from './queries/useStoreApi'
import type { GuestSessionContract } from './queries/useStoreApi'
import { useNotifications } from './useNotifications'
import { usePwa } from './usePwa'
import { useCartStore } from '../../stores/cartStore'
import { useUIStore } from '../../stores/uiStore'
import { lineAtStockLimit } from './cart/stockLimit'
import {
  getGuestCartCreationPromise,
  getLastKnownGuestCartId,
  resetGuestCartCache,
  setGuestCartCreationPromise,
  setLastKnownGuestCartId,
} from './cart/guestCartState'

export interface AddToCartFlowPayload {
  productId: number
  productAttributeId?: number
  quantity?: number
}

// Modułowa pamięć koszyka gościa żyje w leaf-module cart/guestCartState, dzięki
// czemu useCartQuery (queries/useStoreApi) może ją zresetować po wykryciu utraconej
// sesji bez cyklicznego importu useStoreApi <-> useAddToCartFlow. resetGuestCartCache
// re-eksportujemy stąd, by nie łamać istniejących importów (m.in. useCheckoutCartReset).
export { resetGuestCartCache }

function toPositiveNumber(value: unknown) {
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0
}

function readCartId(value: unknown) {
  if (!value || typeof value !== 'object') {
    return 0
  }

  const record = value as {
    cartId?: unknown
    cart_id?: unknown
    cart?: { id?: unknown } | null
  }

  return toPositiveNumber(record.cartId ?? record.cart?.id ?? record.cart_id)
}

function rememberCartId(value: unknown) {
  setLastKnownGuestCartId(readCartId(value))
}

function buildAddToCartPayload(payload: AddToCartFlowPayload) {
  return {
    productId: payload.productId,
    productAttributeId: payload.productAttributeId,
    quantity: payload.quantity,
  }
}

export function useAddToCartFlow() {
  const addToCartMutation = useAddToCartMutation()
  const guestSessionQuery = useGuestSessionQuery()
  const createGuestSessionMutation = useCreateGuestSessionMutation()
  const notifications = useNotifications()
  const cartStore = useCartStore()
  const { data: cartData } = useCartQuery()

  const guestSession = computed<GuestSessionContract | null>(() => guestSessionQuery.data?.value ?? null)
  const stockError = ref<string | null>(null)

  const isPending = computed(() =>
    Boolean(addToCartMutation.isPending?.value || createGuestSessionMutation.isPending?.value),
  )

  const canSubmit = computed(() => !isPending.value)

  async function resolveGuestSession() {
    if (guestSession.value) {
      return guestSession.value
    }

    const refetchResult = await guestSessionQuery.refetch?.()

    return refetchResult?.data ?? guestSessionQuery.data?.value ?? null
  }

  function hasCartFromCartQuery() {
    const cartId = toPositiveNumber(cartData.value?.cartId)
    if (cartId > 0) {
      setLastKnownGuestCartId(cartId)
      return true
    }

    return false
  }

  async function createGuestCartOnce() {
    if (!getGuestCartCreationPromise()) {
      setGuestCartCreationPromise(
        createGuestSessionMutation.mutateAsync()
          .then((result) => {
            rememberCartId(result)
            return result
          })
          .finally(() => {
            setGuestCartCreationPromise(null)
          }),
      )
    }

    const result = await getGuestCartCreationPromise()
    rememberCartId(result)
  }

  async function ensureGuestCart() {
    if (getLastKnownGuestCartId() > 0 || hasCartFromCartQuery()) {
      return
    }

    const session = await resolveGuestSession()

    if (!session) {
      await createGuestCartOnce()
      return
    }

    const idCustomer = toPositiveNumber(session.id_customer)
    const cartId = toPositiveNumber(session.cart?.id ?? session.cart_id)

    if (idCustomer > 0 || cartId > 0 || hasCartFromCartQuery()) {
      if (cartId > 0) {
        setLastKnownGuestCartId(cartId)
      }
      return
    }

    await createGuestCartOnce()
  }

  function isAtStockLimit(payload: AddToCartFlowPayload): boolean {
    const lines = cartData.value?.lines
    if (!lines) return false
    const productIdStr = String(payload.productId)
    const line = lines.find((l) => {
      if (l.productId !== productIdStr) return false
      if (payload.productAttributeId && payload.productAttributeId > 0) {
        return l.id.startsWith(`${productIdStr}-${payload.productAttributeId}-`)
      }
      return true
    })
    if (!line || line.allowOutOfStock) return false
    return lineAtStockLimit(line.stock, line.quantity)
  }

  async function add(payload: AddToCartFlowPayload) {
    stockError.value = null
    if (isAtStockLimit(payload)) {
      stockError.value = 'Brak wystarczającej ilości w magazynie.'
      return
    }
    try {
      await ensureGuestCart()
      const result = await addToCartMutation.mutateAsync(buildAddToCartPayload(payload))
      rememberCartId(result)
      const errors: string[] = Array.isArray((result as { errors?: unknown })?.errors)
        ? ((result as { errors: string[] }).errors)
        : []
      if (errors.length > 0) {
        stockError.value = 'Brak wystarczającej ilości w magazynie.'
        return
      }
      // Po dodaniu zawsze pokazujemy wysuwany drawer koszyka (mobile i desktop).
      cartStore.openDrawer()
      try {
        const { shouldShowPushAsk } = usePwa()
        if (shouldShowPushAsk('add-to-cart')) {
          useUIStore().openPushPermissionSheet('add-to-cart')
        }
      }
      catch {
        // Never break cart flow
      }
    } catch (error) {
      resetGuestCartCache()
      notifications.error('Nie udało się dodać produktu do koszyka.')
      throw error
    }
  }

  return {
    add,
    isAtStockLimit,
    isPending,
    canSubmit,
    stockError,
  }
}
